/* eslint-disable react-hooks/exhaustive-deps */
import { PropsWithChildren, useEffect, useState } from "react";
import { useFormik } from "formik";
import classNames from "clsx";

import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";

import { useApi } from "@/hooks/useApi";
import { transformData } from "@/utils/transformData";
import { useAttendanceId } from "@/hooks/useAttendanceId";
import { useWindowDimensions } from "@/hooks/useWindowDimensions";
import { surgicalPreAnestheticConsultationMap } from "@/utils/mappings/surgicalPreAnestheticConsultation";
import { preAnestheticConsultation } from "@/utils/initialValues/surgicalCenter/preAnestheticConsultation";
import { replaceNullValuesWithEmptyString } from "@/utils/replaceNullValuesWithEmptyString";

import { FormattedFormControl } from "@/components/FormControl/FormattedFormControl";
import { FormGroupRadio } from "@/components/Radio/FormGroupRadio";
import { TagHTMLOrSkeleton } from "@/components/TagHTMLOrSkeleton";
import { SkeletonWrapper } from "@/components/SkeletonWrapper";
import { FormRadio } from "@/components/Radio/FormRadio";
import { ReactSelect } from "@/components/Selects/ReactSelect";
import { Divider } from "@/components/Divider";

export function PreAnestheticConsultationView() {
	const [skeleton, setSkeleton] = useState(true);
	const { attendanceId } = useAttendanceId();
	const { width } = useWindowDimensions();
	const { api } = useApi();

	const formik = useFormik({
		initialValues: preAnestheticConsultation,
		onSubmit: () => {},
	});

	const values = formik.values;

	async function getAllData() {
		try {
			const { data } = await api.get(
				`/surgical-center/${attendanceId}/pre-anesthetic-consultation`
			);

			const transformedData = transformData<SurgicalPreAnestheticConsultation>(
				data,
				surgicalPreAnestheticConsultationMap
			);

			const formattedData = replaceNullValuesWithEmptyString(transformedData);

			formik.setValues(formattedData);
		} finally {
			setSkeleton(false);
		}
	}

	function skeletonInlineWrapper({ children }: PropsWithChildren<unknown>) {
		return <span style={{ marginRight: "2%" }}>{children}</span>;
	}

	useEffect(() => {
		if (attendanceId) {
			getAllData();
		}
	}, [attendanceId]);

	return (
		<>
			<div className="px-5 pb-7">
				<TagHTMLOrSkeleton
					text="Atendimento"
					tag="h6"
					className="font-weight-bold mt-3 mb-6"
					skeleton={skeleton}
					skeletonWidth="20%"
				/>

				<Row>
					<Col xs={6}>
						<TagHTMLOrSkeleton
							text="Médico Responsável:"
							tag="label"
							className="form-label mb-2"
							htmlFor="user-id"
							skeleton={skeleton}
						/>

						<ReactSelect
							isSolid
							isDisabled
							inputId="user-id"
							skeleton={skeleton}
							value={{ value: values.user.id, label: values.user.name }}
						/>
					</Col>

					<Col lg={4} xs={6}>
						<FormattedFormControl
							formattedFormControlClassName="form-control-solid"
							type="datetime-local"
							label="Data do Atendimento:"
							id="attendance-date"
							{...formik.getFieldProps("attendance_date")}
							name="attendance_date"
							skeleton={skeleton}
							readOnly
						/>
					</Col>
				</Row>
			</div>

			<Divider />

			<div className="px-5">
				<TagHTMLOrSkeleton
					text="Anamnese do Paciente"
					tag="h6"
					className="font-weight-bold my-3"
					skeleton={skeleton}
					skeletonWidth="20%"
				/>

				<Row className="mb-3 align-items-center">
					<Col sm={7} className="d-flex align-items-center justify-content-between">
						<FormGroupRadio
							name="previous_anesthesia"
							label="Anestesia anterior:"
							requestedValue={values.previous_anesthesia}
							skeleton={skeleton}
							skeletonWidth="100%"
							containerClassName="w-50"
							inline
						>
							<FormRadio label="Sim" value="1" />
							<FormRadio label="Não" value="0" />
						</FormGroupRadio>
					</Col>

					{Number(values.previous_anesthesia) === 1 && (
						<Col
							sm={5}
							className={classNames(
								"d-flex flex-grow-1 align-items-center gap-3",
								width > 580 && "justify-content-end"
							)}
						>
							<Form.Label className="m-0" htmlFor="description-previous-anesthesia">
								Qual?
							</Form.Label>

							<FormattedFormControl
								id="description-previous-anesthesia"
								className="form-control-solid"
								placeholder="Descreva a cirurgia"
								{...formik.getFieldProps("description_previous_anesthesia")}
								name="description_previous_anesthesia"
								readOnly
							/>
						</Col>
					)}
				</Row>

				<Row className="align-items-center border-top border-gray-300 pt-6 pb-3">
					<Col sm={7} className="d-flex align-items-center justify-content-between">
						<FormGroupRadio
							name="hemotransfusion"
							label="Hemotransfusão:"
							requestedValue={values.hemotransfusion}
							skeleton={skeleton}
							containerClassName="flex-grow-1"
							skeletonWidth="50%"
							inline
						>
							<FormRadio label="Sim" value="1" />
							<FormRadio label="Não" value="0" />
						</FormGroupRadio>
					</Col>
				</Row>

				<Row className="align-items-center border-top border-bottom border-gray-300 pt-6 pb-3">
					<Col
						md={9}
						sm={7}
						className="d-flex align-items-center justify-content-between"
					>
						<FormGroupRadio
							name="asa_classification"
							label="Classificação ASA:"
							labelClassName="mr-2"
							requestedValue={values.asa_classification}
							skeleton={skeleton}
							containerClassName="flex-grow-1"
							skeletonWidth="50%"
							inline
						>
							<FormRadio label="I" value="1" />
							<FormRadio label="II" value="2" />
							<FormRadio label="III" value="3" />
							<FormRadio label="IV" value="4" />
							<FormRadio label="V" value="5" />
						</FormGroupRadio>
					</Col>
				</Row>

				{skeleton ? (
					<div className="py-5">
						<SkeletonWrapper
							count={2}
							width="30%"
							wrapper={skeletonInlineWrapper}
							inline
						/>
					</div>
				) : (
					<div className="d-flex align-items-center gap-3 py-6">
						<Form.Label
							className="font-weight-bold h6 m-0"
							htmlFor="invasive-blood-pressure"
						>
							PAI - Pressão Arterial Invasiva
						</Form.Label>

						<FormattedFormControl
							id="invasive-blood-pressure"
							{...formik.getFieldProps("invasive_blood_pressure")}
							name="invasive_blood_pressure"
							className="form-control-solid"
							skeleton={skeleton}
							readOnly
						/>
					</div>
				)}
			</div>

			<Divider />

			<div className="mt-3 mb-6">
				<div className="px-5">
					<TagHTMLOrSkeleton
						text="Doenças ou sintomas que tem ou teve"
						tag="h6"
						className="font-weight-bold mb-6"
						skeleton={skeleton}
						skeletonWidth="20%"
					/>

					{skeleton ? (
						<SkeletonWrapper count={9} containerClassName="mb-3" />
					) : (
						<Row>
							<Col sm={12} lg={6} className="pb-2">
								<Form.Check
									label="Hipertensão arterial"
									name="diseases_or_symptoms"
									value="Hipertensão arterial"
									checked={values.diseases_or_symptoms.includes(
										"Hipertensão arterial"
									)}
									readOnly
								/>
							</Col>

							<Col sm={12} lg={6} className="pb-2">
								<Form.Check
									label="Doença renal"
									name="diseases_or_symptoms"
									value="Doença Renal"
									checked={values.diseases_or_symptoms.includes("Doença Renal")}
									readOnly
								/>
							</Col>

							<Col sm={12} lg={6} className="pb-2">
								<Form.Check
									label="Cardiopatia"
									name="diseases_or_symptoms"
									value="Cardiopatia"
									checked={values.diseases_or_symptoms.includes("Cardiopatia")}
									readOnly
								/>
							</Col>

							<Col sm={12} lg={6} className="pb-2">
								<Form.Check
									label="Alergia"
									name="diseases_or_symptoms"
									value="Alergia"
									checked={values.diseases_or_symptoms.includes("Alergia")}
									readOnly
								/>
							</Col>

							<Col sm={12} lg={6} className="pb-2">
								<Form.Check
									label="Dispmeia aos esforços-ortopneia"
									name="diseases_or_symptoms"
									value="Dispmeia aos esforços-ortopneia"
									checked={values.diseases_or_symptoms.includes(
										"Dispmeia aos esforços-ortopneia"
									)}
									readOnly
								/>
							</Col>

							<Col sm={12} lg={6} className="pb-2">
								<Form.Check
									label="Convulsão"
									name="diseases_or_symptoms"
									value="Convulsão"
									checked={values.diseases_or_symptoms.includes("Convulsão")}
									readOnly
								/>
							</Col>

							<Col sm={12} lg={6} className="pb-2">
								<Form.Check
									label="Precordialgia típica"
									name="diseases_or_symptoms"
									value="Precordialgia típica"
									checked={values.diseases_or_symptoms.includes(
										"Precordialgia típica"
									)}
									readOnly
								/>
							</Col>

							<Col sm={12} lg={6} className="pb-2">
								<Form.Check
									label="Sangramentos espontâneos (nariz, gengiva, petéquias)"
									name="diseases_or_symptoms"
									value="Sangramentos espontâneos (nariz, gengiva, petéquias)"
									checked={values.diseases_or_symptoms.includes(
										"Sangramentos espontâneos (nariz, gengiva, petéquias)"
									)}
									readOnly
								/>
							</Col>

							<Col sm={12} lg={6} className="pb-2">
								<Form.Check
									label="Diabete mellitus"
									name="diseases_or_symptoms"
									value="Diabete mellitus"
									checked={values.diseases_or_symptoms.includes(
										"Diabete mellitus"
									)}
									readOnly
								/>
							</Col>

							<Col sm={12} lg={6} className="pb-2">
								<Form.Check
									label="Prótese dentária"
									name="diseases_or_symptoms"
									value="Prótese dentária"
									checked={values.diseases_or_symptoms.includes(
										"Prótese dentária"
									)}
									readOnly
								/>
							</Col>

							<Col sm={12} lg={6} className="pb-2">
								<Form.Check
									label="Pneumopatia (TB, asma, DPOC)"
									name="diseases_or_symptoms"
									value="Pneumopatia (TB, asma, DPOC)"
									checked={values.diseases_or_symptoms.includes(
										"Pneumopatia (TB, asma, DPOC)"
									)}
									readOnly
								/>
							</Col>

							<Col sm={12} lg={6} className="pb-2">
								<Form.Check
									label="Possiblidade de gravidez atual"
									name="diseases_or_symptoms"
									value="Possiblidade de gravidez atual"
									checked={values.diseases_or_symptoms.includes(
										"Possiblidade de gravidez atual"
									)}
									readOnly
								/>
							</Col>

							<Col sm={12} lg={6} className="pb-2">
								<Form.Check
									label="Tabagismo"
									name="diseases_or_symptoms"
									value="Tabagismo"
									checked={values.diseases_or_symptoms.includes("Tabagismo")}
									readOnly
								/>
							</Col>

							<Col sm={12} lg={6} className="pb-2">
								<Form.Check
									label="Anestesias anteriores (técnicas)"
									name="diseases_or_symptoms"
									value="Anestesias anteriores (técnicas)"
									checked={values.diseases_or_symptoms.includes(
										"Anestesias anteriores (técnicas)"
									)}
									readOnly
								/>
							</Col>

							<Col sm={12} lg={6} className="pb-2">
								<Form.Check
									label="Etilismo"
									name="diseases_or_symptoms"
									value="Etilismo"
									checked={values.diseases_or_symptoms.includes("Etilismo")}
									readOnly
								/>
							</Col>

							<Col sm={12} lg={6} className="pb-2">
								<Form.Check
									label="Complicações anestésicas pessoal ou familiar (febre)"
									name="diseases_or_symptoms"
									value="Complicações anestésicas pessoal ou familiar (febre)"
									checked={values.diseases_or_symptoms.includes(
										"Complicações anestésicas pessoal ou familiar (febre)"
									)}
									readOnly
								/>
							</Col>

							<Col sm={12} lg={6} className="pb-2">
								<Form.Check
									label="Hepatite"
									name="diseases_or_symptoms"
									value="Hepatite"
									checked={values.diseases_or_symptoms.includes("Hepatite")}
									readOnly
								/>
							</Col>

							<Col sm={12} lg={6} className="pb-2">
								<Form.Check
									label="Outras doenças não citadas"
									name="diseases_or_symptoms"
									value="Outras doenças não citadas"
									checked={values.diseases_or_symptoms.includes(
										"Outras doenças não citadas"
									)}
									readOnly
								/>
							</Col>
						</Row>
					)}
				</div>
			</div>

			<Divider />

			<div className="px-5 pb-10">
				<TagHTMLOrSkeleton
					text="Observações"
					tag="label"
					htmlFor="observations"
					className="font-weight-bold h6 mb-3 pt-4"
					skeleton={skeleton}
					skeletonWidth="20%"
				/>

				<FormattedFormControl
					as="textarea"
					id="observations"
					placeholder="Descreva"
					{...formik.getFieldProps("observations")}
					name="observations"
					className="form-control-solid"
					skeleton={skeleton}
					skeletonHeight={45}
					readOnly
				/>
			</div>
		</>
	);
}
